import React, { createContext, useContext } from 'react';
import type { ATContextProviderProps } from './types';
import type { SelectionObject, ATSelectionCtxType } from './selectionTypes';

const ATSelectionContext = createContext<ATSelectionCtxType<any>>({
  selection: [] as any,
  selectionVersion: 0,
});

export const useATSelection = (): ATSelectionCtxType<any> => {
  const ctx = useContext(ATSelectionContext);
  if (ctx === null) {
    throw new Error('useATSelection must be used within a ATSelectionProvider');
  }
  return ctx;
};

export const ATSelectionContextProvider: React.FC<ATContextProviderProps> = ({
  children,
}) => {
  // Map to track selected items
  const selectionMap = React.useRef<Map<any, any>>(new Map());

  // Debounce timeout ref
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const pendingUpdateRef = React.useRef<(() => void) | null>(null);

  // State to track selection version for re-renders (triggers get() updates)
  const [selectionVersion, setSelectionVersion] = React.useState(0);

  // Helper function to schedule debounced updates
  const scheduleDebouncedUpdate = React.useCallback((updateFn: () => void) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    pendingUpdateRef.current = updateFn;
    debounceTimeoutRef.current = setTimeout(() => {
      if (pendingUpdateRef.current) {
        pendingUpdateRef.current();
        pendingUpdateRef.current = null;
      }
    }, 150);
  }, []);

  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Create selection object once
  const selection = React.useRef<SelectionObject<any>>({
    // Get array or specific item by itemId
    get: (itemId?: any) => {
      if (itemId === undefined) {
        if (selectionMap.current.size === 0) {
          return [];
        }
        return selectionMap.current.values();
      }
      return selectionMap.current.get(itemId);
    },

    getArray: (fn?: (key: any, value: any) => any) => {
      if (fn) {
        const values = [];
        for (const [key, value] of selectionMap.current) {
          values.push(fn(key, value));
        }
        return values;
      }
      return Array.from(selectionMap.current.values());
    },

    getObject: (
      fn?: (key: any, value: any) => any,
      columnAsKey?: keyof any,
    ) => {
      if (fn) {
        const values: Record<any, any> = {};
        for (const [key, value] of selectionMap.current) {
          const result = fn(key, value);
          // flexible key mapping
          if (Array.isArray(result) && result.length === 2) {
            values[result[0]] = result[1];
          } else {
            values[columnAsKey ? value[columnAsKey] : key] = result;
          }
        }
        return values;
      }
      return Object.fromEntries(selectionMap.current.entries());
    },

    getIndex: (index: number) => {
      if (index < 0 || index >= selectionMap.current.size) {
        return undefined;
      }
      let tempIndex = 0;
      for (const [key, value] of selectionMap.current) {
        if (tempIndex === index) {
          return value;
        }
        tempIndex++;
      }
      return undefined;
    },

    // Check if item is selected
    has: (itemId: any) => selectionMap.current.has(itemId),

    // Debounced methods
    set: (
      newSelection: any[] | ((prev: any[]) => any[]),
      rowKey?: keyof any,
    ) => {
      scheduleDebouncedUpdate(() => {
        const currentArray = Array.from(selectionMap.current.values());
        const updatedSelection =
          typeof newSelection === 'function'
            ? newSelection(currentArray)
            : newSelection;
        selectionMap.current.clear();
        if (rowKey !== undefined) {
          updatedSelection.forEach((item) => {
            selectionMap.current.set(item[rowKey], item);
          });
        }
        setSelectionVersion((v) => v + 1);
      });
    },

    add: (item: any, rowKey: keyof any) => {
      if (selectionMap.current.has(item[rowKey])) return;
      scheduleDebouncedUpdate(() => {
        selectionMap.current.set(item[rowKey], item);
        setSelectionVersion((v) => v + 1);
      });
    },

    remove: (item: any, rowKey: keyof any) => {
      scheduleDebouncedUpdate(() => {
        selectionMap.current.delete(item[rowKey]);
        setSelectionVersion((v) => v + 1);
      });
    },

    toggle: (item: any, rowKey: keyof any) => {
      const isSelected = selectionMap.current.has(item[rowKey]);
      scheduleDebouncedUpdate(() => {
        if (isSelected) {
          selectionMap.current.delete(item[rowKey]);
        } else {
          selectionMap.current.set(item[rowKey], item);
        }
        setSelectionVersion((v) => v + 1);
      });
    },

    toggleSingle: (item: any, rowKey: keyof any) => {
      const isSelected = selectionMap.current.has(item[rowKey]);
      scheduleDebouncedUpdate(() => {
        selectionMap.current.clear();
        if (!isSelected) selectionMap.current.set(item[rowKey], item);
        setSelectionVersion((v) => v + 1);
      });
    },

    clear: () => {
      scheduleDebouncedUpdate(() => {
        selectionMap.current.clear();
        setSelectionVersion((v) => v + 1);
      });
    },

    findIndex: (callback: (value: any, key: any, index: number) => boolean) => {
      let tempIndex = 0;
      selectionMap.current.forEach((value, key) => {
        if (callback(value, key, tempIndex) === true) {
          return tempIndex;
        }
        tempIndex++;
      });
      return undefined;
    },

    find: (callback: (value: any, key: any, index: number) => boolean) => {
      let tempIndex = 0;
      selectionMap.current.forEach((value, key) => {
        if (callback(value, key, tempIndex)) {
          return value;
        }
        tempIndex++;
      });
      return undefined;
    },

    some: (callback: (value: any, key: any, index: number) => boolean) => {
      let tempIndex = 0;
      selectionMap.current.forEach((value, key) => {
        if (callback(value, key, tempIndex)) {
          return true;
        }
        tempIndex++;
      });
      return false;
    },

    every: (callback: (value: any, key: any, index: number) => boolean) => {
      let tempIndex = 0;
      selectionMap.current.forEach((value, key) => {
        if (!callback(value, key, tempIndex)) {
          return false;
        }
        tempIndex++;
      });
      return true;
    },

    // forEach
    forEach: (callback: (value: any, key: any, index: number) => void) => {
      let tempIndex = 0;
      selectionMap.current.forEach((value, key) => {
        callback(value, key, tempIndex);
        tempIndex++;
      });
    },

    map: <R,>(callback: (value: any, key: any, index: number) => R): R[] => {
      const results: R[] = [];
      let tempIndex = 0;
      selectionMap.current.forEach((value, key) => {
        results.push(callback(value, key, tempIndex));
        tempIndex++;
      });
      return results;
    },

    filter: (callback: (value: any, key: any, index: number) => boolean) => {
      const results: any[] = [];
      let tempIndex = 0;
      selectionMap.current.forEach((value, key) => {
        if (callback(value, key, tempIndex)) {
          results.push(value);
        }
        tempIndex++;
      });
      return results;
    },

    reduce: <R,>(
      callback: (accumulator: R, value: any, key: any, index: number) => R,
      initialValue: R,
    ): R => {
      let accumulator = initialValue;
      let tempIndex = 0;
      selectionMap.current.forEach((value, key) => {
        accumulator = callback(accumulator, value, key, tempIndex);
        tempIndex++;
      });
      return accumulator;
    },

    // Immediate methods (non-debounced)
    addImmediate: (item: any, rowKey: keyof any) => {
      const key = item[rowKey];
      if (!selectionMap.current.has(key)) {
        selectionMap.current.set(key, item);
        setSelectionVersion((v) => v + 1);
      }
    },

    removeImmediate: (item: any, rowKey: keyof any) => {
      const key = item[rowKey];
      selectionMap.current.delete(key);
      setSelectionVersion((v) => v + 1);
    },

    toggleImmediate: (item: any, rowKey: keyof any) => {
      const key = item[rowKey];
      const isSelected = selectionMap.current.has(key);
      if (isSelected) {
        selectionMap.current.delete(key);
      } else {
        selectionMap.current.set(key, item);
      }
      setSelectionVersion((v) => v + 1);
    },

    toggleSingleImmediate: (item: any, rowKey: keyof any) => {
      const key = item[rowKey];
      const isSelected = selectionMap.current.has(key);
      selectionMap.current.clear();
      if (!isSelected) selectionMap.current.set(key, item);
      setSelectionVersion((v) => v + 1);
    },

    clearImmediate: () => {
      selectionMap.current.clear();
      setSelectionVersion((v) => v + 1);
    },
  } as any).current;

  // Add Map-like properties for direct access to selectionMap
  Object.defineProperty(selection, 'size', {
    get: () => selectionMap.current.size,
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(selection, 'values', {
    get: () => () => selectionMap.current.values(),
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(selection, 'keys', {
    get: () => () => selectionMap.current.keys(),
    enumerable: true,
    configurable: true,
  });

  Object.defineProperty(selection, 'entries', {
    get: () => () => selectionMap.current.entries(),
    enumerable: true,
    configurable: true,
  });

  const value = React.useMemo(
    () => ({
      selection,
      selectionVersion, // Include version to trigger re-renders when selection changes
    }),
    [selection, selectionVersion],
  );

  return (
    <ATSelectionContext.Provider value={value}>
      {children}
    </ATSelectionContext.Provider>
  );
};
